
  import { Component, Prop, Watch } from 'vue-property-decorator'
  import { mapActions, mapGetters, mapMutations } from 'vuex'

  import initials from 'initials'
  import { fixText } from '@/utils/general'
  import { PersonFormatted } from '@/store/persons/person'
  import LogOut from '@/components/LogOut.vue'
  import SpeedTestNotification from '@/components/SpeedTestNotification.vue'
  import { GForm } from '@/components/forms/GForm'

@Component({
  components: { SpeedTestNotification, LogOut },
  computed: {
    ...mapGetters('user', ['isLoggedIn', 'user', 'rolesName', 'roles']),
    ...mapGetters('app', ['isMini']),
    ...mapGetters('resources/form', ['crumbs']),
  },
  methods: {
    ...mapMutations('resources/form', ['setBackup']),
    ...mapActions('user', ['logout']),
    ...mapMutations('persons', ['setFoundPerson', 'setResetPhone', 'setRutInfo', 'setRutError', 'setId']),
    ...mapActions('resources/form', ['setCrumbs']),
  },
})
  export default class ActiveUser extends GForm {
  @Prop({ default: 'left bottom' }) origin!: string

  // Métodos del store

  setFoundPerson!: (person: PersonFormatted | null) => void;
  setResetPhone!: (phone) => void
  setId!: (id) => void
  setRutError!: (data: boolean) => void;
  logout!: () => void;

  // Propiedades del store
  public isLoggedIn!: boolean;
  isMini!: boolean;

  // Estado local
  menuOpen = false
  openDialogSpeedTest = false
  useDefaultColor = false
  isTourActive = false
  isAvailable = false

  async mounted () {
    document.addEventListener('open-perfil-menu', this.handleOpenProfileMenu)
  }

  beforeDestroy () {
    document.removeEventListener('open-perfil-menu', this.handleOpenProfileMenu)
  }

  get isStaff () {
    const { roles } = this

    return roles.some(r => r.slug === 'staff')
  }

  @Watch('isMini')
  onDrawerStateChange (newValue: boolean, oldValue: boolean): void {
    // Cerrar el menú cuando cambie el estado del drawer
    if (newValue !== oldValue && this.menuOpen && !this.isTourActive) {
      this.menuOpen = false
    }

    // Forzar actualización en el siguiente tick para asegurar sincronización
    this.$nextTick(() => {
      this.$forceUpdate()
    })
  }

  handleOpenProfileMenu (): void {
    this.isTourActive = true
    this.menuOpen = true
  }

  get initials (): string | string[] {
    const { displayName, email } = this.user
    return initials((displayName as string) || (email as string))
  }

  get displayName (): string {
    const { displayName, email } = this.user
    return this.truncateText((displayName as string) || (email as string))
  }

  get isSysOps () {
    const { roles } = this

    return roles.some(role => role.slug === 'sysop')
  }

  get isDev (): boolean {
    return process.env.VUE_APP_DEV === 'true'
  }

  truncateText (val: string): string {
    return fixText(val)
  }

  toggleMenu (): void {
    this.menuOpen = !this.menuOpen
  }

  async openEnterprise () {
    const { isSysOps } = this

    if (['enterprise', 'person'].includes(this.$route.path) || !isSysOps) {
      return
    }

    this.setCrumbs([])
    this.initValues()
    const { metadata } = this.getForm('Person', 'person')

    const person = await this.fetchData({
      query: { name: 'findLite', model: 'Person' },
      filter: { name_person_type: { _eq: 'system' } },
      force: true,
    })

    this.setCustomCrumbs(metadata, 'Editar Empresa')
    if (person[0]?.id) {
      await this.$router.push({
        name: 'generic-person-system',
        params: { uid: person[0].id },
      }).catch(() => {
      })
    }
  }

  async openPerson () {
    this.menuOpen = false

    if (this.$route.path.includes('person')) {
      return
    }

    // Esperar un tick para asegurar que el estado se sincronice
    await this.$nextTick()

    this.setCrumbs([])
    this.initValues()
    const { metadata } = this.getForm('Person', 'person')
    const user = await this.fetchData({
      query: { name: 'findLite', model: 'User' },
      filter: { email: { _eq: this.user.email } },
    })

    const person = await this.fetchData({
      query: { name: 'findLite', model: 'Person' },
      filter: { user: { id: { _eq: user[0].id } } },
    })

    this.setCustomCrumbs(metadata, 'Editar Persona')
    await this.$router.push({
      name: 'generic-person',
      params: { uid: person[0].id },
    }).catch(() => {
    })
  }

  setCustomCrumbs (metadata, title) {
    this.setCrumbs([{ resource: metadata, title }])
  }

  initValues () {
    this.setId(null)
    this.setFoundPerson(null)
    this.setResetPhone(null)
    this.setRutInfo(null)
    this.setRutError(false)
    this.setBackup(null)
  }

  signOut (): void {
    this.menuOpen = false
    this.$nextTick(() => {
      this.logout()
    })
  }

  openSpeedTest () {
    this.menuOpen = false
    this.$nextTick(() => {
      this.openDialogSpeedTest = true
      window.open('https://www.fast.com/', '_blank')
    })
  }

  togglePrimaryColor () {
    const vuetify = this.$vuetify
    const newColor = this.useDefaultColor ? '#6185DB' : '#000000'
    vuetify.theme.themes.light.primary = newColor
  }

  async onAvailabilityChange (isAvailable: boolean): Promise<void> {
    if (!this.idEmployee || !this.isStaff) return
    // Observador para cambios en la disponibilidad del usuario

    const status = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: {
        process: {
          table_name: { _eq: 'employee' },
        },
      },
    })

    const activeStatus = status.find(s => s.status.name === 'active')
    const noActiveStatus = status.find(s => s.status.name === 'not_operational')

    await this.pushData({
      model: 'Employee',
      fields: {
        id: this.idEmployee,
        id_process_status: isAvailable ? activeStatus.id : noActiveStatus.id,
      },
    })
  }

  @Watch('idEmployee', { immediate: true })
  async onIdEmployeeChange (newValue: number, oldValue: number): Promise<void> {
    if (!newValue || !this.isStaff) return

    const employeeStatus = (await this.fetchData({
      query: { name: 'find', model: 'Employee' },
      filter: {
        id: { _eq: newValue },
      },
      force: true,
    }))[0]

    this.isAvailable = employeeStatus.status.isActive
  }
  }
